import { requestJson, withQuery } from "../http/httpClient";
import { readArray } from "../http/jsonDecoding";
import {
  readLabelledReference,
  readVersionLabel,
  readVersionPlan,
  readWorkflowEditChoice,
  type LabelledReference,
  type VersionLabel,
  type VersionPlan,
  type WorkflowEditChoice,
} from "./versionDecoders";

/**
 * Version labels (ADR-0033, built provisionally, awaiting acceptance): what a change set
 * proposes, the labels of exact versions, the content versions that changed what a
 * Workflow reads (D5), and F33's update or derive.
 */

const projectPath = (projectId: string): `/${string}` => `/projects/${encodeURIComponent(projectId)}`;

/** "Draft based on 1.2.0 · proposes 1.3.0 · why", for the project content and each item the change set changes. */
export function getVersionPlan(changeSetId: string, signal?: AbortSignal): Promise<VersionPlan> {
  return requestJson({
    method: "GET",
    path: `/content/change-sets/${encodeURIComponent(changeSetId)}/version-plan`,
    signal,
    decodeResponse: readVersionPlan,
  });
}

/** Exact versions to label: item versions `<item id>@<blob>`, collection versions `<collection id>@<commit>`. */
export interface LabelQuery {
  readonly itemVersions: readonly string[];
  readonly collectionVersions: readonly string[];
}

/** The label of each exact version, in the order asked; at most 100 references a request. */
export function getVersionLabels(projectId: string, query: LabelQuery, signal?: AbortSignal): Promise<readonly LabelledReference[]> {
  const parameters = new URLSearchParams();
  for (const reference of query.itemVersions) parameters.append("item_version", reference);
  for (const reference of query.collectionVersions) parameters.append("collection_version", reference);
  return requestJson({
    method: "GET",
    path: `${projectPath(projectId)}/version-labels?${parameters.toString()}`,
    signal,
    decodeResponse: (body) => readArray(body, "labelled references", readLabelledReference),
  });
}

/** The project content's versions whose change touched something the Workflow reads (D5), newest first. */
export function listWorkflowContentVersions(projectId: string, workflow: string, signal?: AbortSignal): Promise<readonly VersionLabel[]> {
  return requestJson({
    method: "GET",
    path: withQuery(`${projectPath(projectId)}/content-versions`, { workflow }),
    signal,
    decodeResponse: (body) => readArray(body, "content versions", readVersionLabel),
  });
}

/** Whether an edit of the Workflow updates it or saves a derived one, and which is the default (F33). */
export function getWorkflowEditChoice(projectId: string, workflow: string, changeSetId: string | null, signal?: AbortSignal): Promise<WorkflowEditChoice> {
  return requestJson({
    method: "GET",
    path: withQuery(`${projectPath(projectId)}/workflow-edits`, { workflow, change_set: changeSetId }),
    signal,
    decodeResponse: readWorkflowEditChoice,
  });
}
